import { BellIcon } from '@heroicons/react/outline';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

import clsxm from '@/lib/clsxm';

import { loadWhatsNew } from '@/components/dashboard/whatsNewFeed';
import { SimpleTooltip } from '@/components/ui/tooltip';

import { nav, type NavVariant, useNavTrack } from './navUi';

/**
 * The What's New bell with its unread dot. The changelog (`atoms/WhatsNew`,
 * ~100 KB) loads once the browser is idle, not with the page: the bell only
 * needs the newest entry's title to know whether there is something new.
 * "Seen" is the same localStorage key the old bell used.
 */
export default function WhatsNewBell({
  variant,
  className,
}: {
  variant: NavVariant;
  className?: string;
}) {
  const { t } = useLocale('dashboard');
  const router = useRouter();
  const track = useNavTrack(variant);
  const [lastSeen, setLastSeen] = useLocalStorage<string>(
    'whats-new-last-seen-sender',
    '',
  );
  const [latest, setLatest] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      loadWhatsNew()
        .then((items) => {
          if (!cancelled) setLatest(items[0]?.key ?? null);
        })
        .catch(() => undefined);
    // Safari has no requestIdleCallback.
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(load, { timeout: 4000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(id);
      };
    }
    const id = setTimeout(load, 2000);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, []);

  const hasNew = !!latest && lastSeen !== latest;

  const open = () => {
    track('bell', '/dashboard#whats-new', { unread: hasNew });
    loadWhatsNew()
      .then((items) => items[0] && setLastSeen(items[0].key))
      .catch(() => undefined);
    if (router.pathname === '/dashboard') {
      document
        .getElementById('whats-new')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      router.push('/dashboard#whats-new');
    }
  };

  const size = variant === 'sidebar' ? 'h-8 w-8' : 'h-10 w-10';

  return (
    <SimpleTooltip
      title={t("What's New")}
      side={variant === 'rail' ? 'right' : 'bottom'}
    >
      <button
        type='button'
        onClick={open}
        aria-label={t("What's New")}
        className={clsxm(
          'relative flex shrink-0 items-center justify-center rounded-lg transition-colors',
          size,
          nav.quiet,
          'hover:bg-[#f4f4fb] hover:text-[#10101c] dark:hover:bg-white/[0.06] dark:hover:text-white',
          nav.focus,
          className,
        )}
      >
        <BellIcon className='h-[18px] w-[18px]' aria-hidden='true' />
        {hasNew && (
          <span
            aria-hidden='true'
            className={clsxm(
              'absolute h-2 w-2 rounded-full bg-primary-500 ring-2 ring-white dark:ring-slate-900',
              variant === 'sidebar' ? 'end-1.5 top-1.5' : 'end-2.5 top-2.5',
            )}
          />
        )}
        {hasNew && <span className='sr-only'>{t('New')}</span>}
      </button>
    </SimpleTooltip>
  );
}
